import type { AskHelixContextKindDto, AskHelixSubjectKindDto } from './ask-helix';

export type AskHelixSurfaceId =
  | 'portfolio_planner'
  | 'execution_cockpit'
  | 'scenario_lab'
  | 'org_insights'
  | 'org_health'
  | 'vcb_objective'
  | 'tenant_workspace'
  | 'revenue_goal' | 'objective'
  | 'decision_agent_module'
  | 'executive_brief'
  | 'strategy_brief'
  | 'finance_brief'
  | 'operations_brief'
  | 'org_brief'
  | 'portfolio_brief'
  | 'decision_memo'
  | 'decision_memos'
  | 'board_rollup';

export type AskHelixSurfaceAvailability = 'wired' | 'registered_only';
export type AskHelixSurfaceDataClassification = 'internal' | 'finance_sensitive' | 'people_sensitive';
export type AskHelixSurfaceScopeBinding =
  | 'strategy'
  | 'operating_scope'
  | 'portfolio_scope'
  | 'org_state'
  | 'decision_scope';

export type AskHelixSurfaceRegistryEntry = {
  id: AskHelixSurfaceId;
  title: string;
  routePath: string;
  apiPath: string;
  askHelixAvailability: AskHelixSurfaceAvailability;
  dataClassification: AskHelixSurfaceDataClassification;
  scopeBinding: AskHelixSurfaceScopeBinding;
  requiredPermissions: string[];
  supportedContextKinds: AskHelixContextKindDto[];
  subjectKinds: AskHelixSubjectKindDto[];
};

const ASK_HELIX_SURFACE_REGISTRY: AskHelixSurfaceRegistryEntry[] = [
  {
    id: 'objective', title: 'Objective', routePath: '/ask', apiPath: '/v1/ask',
    askHelixAvailability: 'wired', dataClassification: 'internal', scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read', 'ai:use'], supportedContextKinds: ['objective'], subjectKinds: ['strategy'],
  },
  {
    id: 'revenue_goal', title: 'Revenue Goal', routePath: '/ask', apiPath: '/v1/views/revenue',
    askHelixAvailability: 'wired', dataClassification: 'finance_sensitive', scopeBinding: 'strategy',
    requiredPermissions: ['finance:read', 'ai:use'], supportedContextKinds: ['revenue_goal'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'portfolio_planner',
    title: 'Portfolio Planner Source',
    routePath: '/planning/portfolio-planner',
    apiPath: '/v1/portfolio-planner/plans/:planId',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'portfolio_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['portfolio_planner'],
    subjectKinds: [],
  },
  {
    id: 'execution_cockpit',
    title: 'Execution Cockpit Source',
    routePath: '/strategy-studio/strategies/[id]/execution-cockpit',
    apiPath: '/v1/execution-cockpit/runs/:runId',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'operating_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['execution_cockpit'],
    subjectKinds: [],
  },
  {
    id: 'scenario_lab',
    title: 'Scenario Lab Source',
    routePath: '/strategy-studio/strategies/[id]/scenario-lab',
    apiPath: '/v1/scenario-lab/runs/:runId',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['scenario_lab'],
    subjectKinds: [],
  },
  {
    id: 'org_insights',
    title: 'Org Insights Source',
    routePath: '/strategy-studio/strategies/[id]/org-insights',
    apiPath: '/v1/org-insights/runs/:runId',
    askHelixAvailability: 'wired',
    dataClassification: 'people_sensitive',
    scopeBinding: 'org_state',
    requiredPermissions: ['strategy:read', 'ai:use', 'org:read'],
    supportedContextKinds: ['org_insights'],
    subjectKinds: [],
  },
  {
    id: 'org_health',
    title: 'Org Health Source',
    routePath: '/strategy-studio/org-health',
    apiPath: '/v1/org-health/states/:orgStateId',
    askHelixAvailability: 'wired',
    dataClassification: 'people_sensitive',
    scopeBinding: 'org_state',
    requiredPermissions: ['strategy:read', 'ai:use', 'org:read'],
    supportedContextKinds: ['org_health'],
    subjectKinds: [],
  },
  {
    id: 'vcb_objective',
    title: 'Value Creation Blueprint Objective Source',
    routePath: '/strategy-studio/strategies/[id]/blueprint-runs',
    apiPath: '/v1/blueprint-runs/runs/:runId/vcb-synthesis',
    askHelixAvailability: 'wired',
    dataClassification: 'finance_sensitive',
    scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['vcb_objective'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'tenant_workspace',
    title: 'Tenant Workspace Source',
    routePath: '/workspace',
    apiPath: '/v1/ask-helix/workspace/sessions/:sessionId/runtime',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['tenant_workspace'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'decision_agent_module',
    title: 'Decision Agent Module Source',
    routePath: '/decision-agent-modules',
    apiPath: '/v1/ask-helix/query',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'decision_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['decision_agent_module'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'executive_brief',
    title: 'Executive Brief',
    routePath: '/executive-summaries',
    apiPath: '/v1/executive-brief/executive',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'operating_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['execution_cockpit', 'portfolio_planner', 'org_insights', 'org_health'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'strategy_brief',
    title: 'Strategy Brief',
    routePath: '/executive-summaries/strategy-brief',
    apiPath: '/v1/executive-brief/strategy',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read'],
    supportedContextKinds: ['portfolio_planner', 'execution_cockpit', 'scenario_lab', 'org_insights', 'org_health'],
    subjectKinds: ['strategy', 'capability', 'skill'],
  },
  {
    id: 'finance_brief',
    title: 'Finance Brief',
    routePath: '/executive-summaries/finance-brief',
    apiPath: '/v1/executive-brief/finance',
    askHelixAvailability: 'wired',
    dataClassification: 'finance_sensitive',
    scopeBinding: 'strategy',
    requiredPermissions: ['strategy:read', 'ai:use', 'finance:read'],
    supportedContextKinds: ['portfolio_planner', 'execution_cockpit', 'scenario_lab'],
    subjectKinds: ['strategy'],
  },
  {
    id: 'operations_brief',
    title: 'Operations Brief',
    routePath: '/executive-summaries/operations-brief',
    apiPath: '/v1/executive-brief/operations',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'operating_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['execution_cockpit'],
    subjectKinds: ['project'],
  },
  {
    id: 'org_brief',
    title: 'Org Brief',
    routePath: '/executive-summaries/org-brief',
    apiPath: '/v1/executive-brief/organization',
    askHelixAvailability: 'wired',
    dataClassification: 'people_sensitive',
    scopeBinding: 'org_state',
    requiredPermissions: ['strategy:read', 'ai:use', 'org:read'],
    supportedContextKinds: ['org_insights', 'org_health'],
    subjectKinds: ['person', 'leader', 'open_position', 'capability', 'skill', 'strategy'],
  },
  {
    id: 'portfolio_brief',
    title: 'Portfolio Brief',
    routePath: '/executive-summaries/portfolio-brief',
    apiPath: '/v1/executive-brief/portfolio',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'portfolio_scope',
    requiredPermissions: ['strategy:read', 'ai:use'],
    supportedContextKinds: ['portfolio_planner'],
    subjectKinds: ['initiative', 'project', 'capability'],
  },
  {
    id: 'decision_memo',
    title: 'Decision Memo',
    routePath: '/executive-summaries/decision-memo',
    apiPath: '/v1/decision-ledger/strategy/:strategyId/decision',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'decision_scope',
    requiredPermissions: ['strategy:read'],
    supportedContextKinds: ['execution_cockpit', 'portfolio_planner', 'scenario_lab', 'decision_ledger'],
    subjectKinds: [],
  },
  {
    id: 'decision_memos',
    title: 'Decision Memos',
    routePath: '/executive-summaries/decision-memos',
    apiPath: '/v1/decision-ledger/strategy/:strategyId/review-queue',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'decision_scope',
    requiredPermissions: ['strategy:read'],
    supportedContextKinds: ['execution_cockpit', 'portfolio_planner', 'scenario_lab'],
    subjectKinds: [],
  },
  {
    id: 'board_rollup',
    title: 'Board Rollup',
    routePath: '/executive-summaries/board-rollup',
    apiPath: '/v1/decision-ledger/strategy/:strategyId/board-rollup',
    askHelixAvailability: 'wired',
    dataClassification: 'internal',
    scopeBinding: 'decision_scope',
    requiredPermissions: ['strategy:read'],
    supportedContextKinds: ['execution_cockpit', 'portfolio_planner', 'scenario_lab', 'org_insights', 'org_health'],
    subjectKinds: ['strategy'],
  },
];

export function listAskHelixSurfaces(): AskHelixSurfaceRegistryEntry[] {
  return ASK_HELIX_SURFACE_REGISTRY.slice();
}

export function getAskHelixSurface(surfaceId: AskHelixSurfaceId): AskHelixSurfaceRegistryEntry | null {
  return ASK_HELIX_SURFACE_REGISTRY.find((surface) => surface.id === surfaceId) ?? null;
}
